import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";
import { AttendanceFeature } from "@/features/attendance";
import { AttendanceViewTabs } from "@/features/attendance/AttendanceViewTabs";
import { OfficeTimePage } from "@/features/office-time/OfficeTimePage";
import { usePermissions } from "@/entities/session/hooks/usePermissions";
import { PERMISSIONS } from "@/shared/config/permissions";

const searchSchema = z.object({
  employeeUserId: z.coerce.number().optional(),
  date: z.string().optional(),
  attendanceId: z.coerce.number().optional(),
  /**
   * Scopes the page to one shift. Always sent alongside `date`, which is what
   * actually brings the shift's records into range — the list defaults to the
   * last seven days, so a shift outside that window would otherwise be
   * filtered down to nothing that was never fetched.
   */
  shiftId: z.coerce.number().optional(),
  view: z.enum(["shifts", "office"]).optional().catch(undefined),
});

function AttendancePage() {
  const { view, ...search } = Route.useSearch();
  const { hasPermission } = usePermissions();
  const canSeeShifts = hasPermission(PERMISSIONS.SchedulerAttendanceView);
  const canSeeOffice = hasPermission(PERMISSIONS.OfficeTimeManage);
  const showOffice = canSeeOffice && (view === "office" || !canSeeShifts);
  const tabs = canSeeShifts && canSeeOffice
    ? <AttendanceViewTabs view={showOffice ? "office" : "shifts"} />
    : undefined;

  return showOffice
    ? <OfficeTimePage viewTabs={tabs} />
    : <AttendanceFeature initialSearch={search} viewTabs={tabs} />;
}

export const Route = createFileRoute("/scheduler/attendance/")({
  component: AttendancePage,
  staticData: { authorize: true },
  validateSearch: searchSchema,
});
